import type { BadgeProps } from "@/v2-backoffice-client/components/badge"

// Human label for the B2C phoneType enum.
const PHONE_TYPE_LABELS: Record<string, string> = {
  mobile: "Mobile",
  alternateMobile: "Alternate mobile",
  office: "Office",
}

export function getPhoneTypeLabel(phoneType: string): string {
  return PHONE_TYPE_LABELS[phoneType] ?? phoneType
}

export type SmsSignInStateInfo = {
  /** Short badge label. */
  label: string
  variant: BadgeProps["variant"]
  /** Plain-English explanation for the tooltip. */
  description: string
}

// MS Graph's `smsSignInState` describes whether the number can be used for SMS
// *sign-in* (phone number as a primary login method) — NOT whether it receives
// MFA codes. A number still works for MFA in every state except where noted, so
// most of these are neutral/informational rather than warnings. We translate
// the raw enum (e.g. "notAllowedByPolicy") into operator-friendly language.
const SMS_SIGN_IN_STATES: Record<string, SmsSignInStateInfo> = {
  ready: {
    label: "SMS sign-in on",
    variant: "positive",
    description:
      "This number can be used to sign in by SMS and to receive MFA codes.",
  },
  notAllowedByPolicy: {
    label: "SMS sign-in off",
    variant: "neutral",
    description:
      "Signing in by SMS is turned off by tenant policy — this is the normal default and does not affect MFA. The number still receives MFA codes.",
  },
  notConfigured: {
    label: "SMS sign-in off",
    variant: "neutral",
    description:
      "SMS sign-in isn't set up for this number. It still receives MFA codes.",
  },
  notEnabled: {
    label: "SMS sign-in off",
    variant: "neutral",
    description:
      "SMS sign-in isn't enabled for this number. It still receives MFA codes.",
  },
  notSupported: {
    label: "SMS sign-in unsupported",
    variant: "neutral",
    description:
      "This number type can't be used for SMS sign-in. It still receives MFA codes.",
  },
  phoneNumberNotUnique: {
    label: "Number not unique",
    variant: "warning",
    description:
      "This number is registered to more than one user, so it can't be used for SMS sign-in — and may point to a shared account.",
  },
}

// getSmsSignInStateInfo maps a raw smsSignInState to a friendly label, severity,
// and description. Returns null when there's no state to show. Unknown future
// values fall back to a neutral badge carrying the raw value.
export function getSmsSignInStateInfo(
  state: string | null | undefined,
): SmsSignInStateInfo | null {
  if (!state) return null
  return (
    SMS_SIGN_IN_STATES[state] ?? {
      label: `SMS: ${state}`,
      variant: "neutral",
      description:
        "Reported SMS sign-in state from Microsoft Entra. This does not affect whether the number receives MFA codes.",
    }
  )
}
